/**
 * Mail queue viewer. In the emulator every send is handled by MockTransport, so entries
 * land with status "mocked" and the rendered body is visible here — that is how you check
 * notification content without a real Microsoft Graph app registration.
 */

import { useState } from 'react';
import { CallStatus, useCallable } from '../../lib/useCallable';
import { useCollection } from '../../lib/useFirestore';
import { formatTs } from '../MatterList';
import { mailErrorHint } from '../../lib/mailErrorHints';

const TONES = {
  mocked: 'bg-violet-100 dark:bg-violet-900 text-violet-800 dark:text-violet-100',
  sent: 'bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-100',
  queued: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100',
  sending: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100',
  failed: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
};

/**
 * Order-independent array equality, mirroring functions/src/lib/arrays.js's arraysEqual
 * (kept as a tiny local copy rather than a shared import — this file has no build-time
 * link to the functions package). Used to detect whether test mode redirected a send,
 * i.e. whether `originalTo`/`originalCc` differ from the persisted `to`/`cc`.
 */
function arraysEqual(a, b) {
  const left = Array.isArray(a) ? a : [];
  const right = Array.isArray(b) ? b : [];
  if (left.length !== right.length) return false;
  const sortedLeft = [...left].sort();
  const sortedRight = [...right].sort();
  return sortedLeft.every((value, i) => value === sortedRight[i]);
}

/** True when this queue entry's actual recipient differs from what was originally addressed. */
function wasRedirected(item) {
  if (!item.originalTo) return false;
  return !arraysEqual(item.to, item.originalTo) || !arraysEqual(item.cc || [], item.originalCc || []);
}

export default function AdminMailQueue() {
  const mail = useCollection('mailQueue', [['orderBy', 'createdAt', 'desc']]);
  const { run, busy, error, saved } = useCallable();
  const testMail = useCallable();
  const [open, setOpen] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [testResult, setTestResult] = useState(null);

  const sendTest = async (event) => {
    event.preventDefault();
    setTestResult(null);
    const outcome = await testMail.run('adminSendTestMail', { to: testTo.trim() });
    if (outcome) setTestResult(outcome);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {mail.data.length} message{mail.data.length === 1 ? '' : 's'}
        </p>
        <CallStatus busy={busy} error={error} saved={saved} />
      </div>

      <form
        onSubmit={sendTest}
        className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-sm"
      >
        <label htmlFor="test-mail-to" className="text-sm font-medium text-slate-700 dark:text-slate-200">
          Send test email to
        </label>
        <input
          id="test-mail-to"
          type="email"
          required
          placeholder="name@aalrr.com"
          value={testTo}
          onChange={(e) => setTestTo(e.target.value)}
          className="min-w-[220px] rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
        />
        <button
          type="submit"
          disabled={testMail.busy || !testTo.trim()}
          className="rounded bg-firm-700 dark:bg-firm-500 px-3 py-1 text-sm font-medium text-white hover:bg-firm-800 dark:hover:bg-firm-400 disabled:opacity-40"
        >
          {testMail.busy ? 'Sending…' : 'Send test'}
        </button>
        {testResult && (
          <span
            className={`text-xs font-medium ${
              testResult.status === 'sent'
                ? 'text-emerald-600 dark:text-emerald-400'
                : testResult.status === 'mocked'
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-red-600 dark:text-red-400'
            }`}
          >
            {testResult.status === 'sent'
              ? `Sent via ${testResult.transport || 'graph'}`
              : testResult.status === 'mocked'
                ? `Mocked (not really sent): ${testResult.detail || ''}`
                : `Failed: ${testResult.detail || 'unknown error'}`}
          </span>
        )}
        {testMail.error && <span className="text-xs text-red-600 dark:text-red-400">{testMail.error}</span>}
      </form>

      <ul className="divide-y divide-slate-100 rounded-lg bg-white dark:bg-slate-900 shadow">
        {mail.data.map((item) => (
          <li key={item.id} className="px-4 py-3">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">{item.subject}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  to {(item.to || []).join(', ')} · {formatTs(item.createdAt)} · {item.template || item.kind}
                </p>
                {/* Who was copied is part of who it went to. Without it, "were they on it?" had no
                    answer here, only in the sending mailbox's Sent Items. In test mode this is the
                    list actually used; the addressed one is in the amber note below. */}
                {(item.cc || []).length > 0 && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">cc {item.cc.join(', ')}</p>
                )}
                {wasRedirected(item) && (
                  <p className="mt-0.5 inline-flex items-center gap-1 rounded border border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-950 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-200">
                    TEST MODE — redirected from {(item.originalTo || []).join(', ') || '(none)'}
                    {(item.originalCc || []).length ? ` (cc: ${item.originalCc.join(', ')})` : ''}
                  </p>
                )}
                {item.error && (
                  <>
                    <p className="text-xs text-red-600 dark:text-red-400">{item.error}</p>
                    {/* Added under the raw error, never instead of it: the exact text is what you
                        search for and what you quote to whoever can fix it. */}
                    {mailErrorHint(item.error) && (
                      <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
                        {mailErrorHint(item.error)}
                      </p>
                    )}
                  </>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${
                    TONES[item.status] || 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200'
                  }`}
                >
                  {item.status}
                </span>
                <button
                  type="button"
                  className="text-xs text-firm-700 dark:text-firm-100 hover:underline"
                  onClick={() => setOpen(open === item.id ? null : item.id)}
                >
                  {open === item.id ? 'Hide' : 'View'}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  className="text-xs text-firm-700 dark:text-firm-100 hover:underline disabled:opacity-40"
                  onClick={() => run('adminResendMail', { mailId: item.id })}
                >
                  Resend
                </button>
              </div>
            </div>
            {open === item.id && (
              <div className="mt-3 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-3">
                {wasRedirected(item) && (
                  <p className="mb-2 rounded border border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-950 px-2 py-1 text-xs font-medium text-amber-800 dark:text-amber-200">
                    Actually sent to <strong>{(item.to || []).join(', ')}</strong> — originally addressed to{' '}
                    {(item.originalTo || []).join(', ') || '(none)'}
                    {(item.originalCc || []).length ? ` (cc: ${item.originalCc.join(', ')})` : ''}.
                  </p>
                )}
                <div
                  className="prose prose-sm max-w-none text-sm"
                  // Safe: functions/src/mail/templates.js HTML-escapes every interpolated
                  // value, so the only markup here is the template's own.
                  dangerouslySetInnerHTML={{ __html: item.html || '' }}
                />
                {item.text && (
                  <pre className="mt-2 whitespace-pre-wrap text-xs text-slate-600 dark:text-slate-300">{item.text}</pre>
                )}
              </div>
            )}
          </li>
        ))}
        {mail.data.length === 0 && (
          <li className="px-4 py-6 text-sm text-slate-500 dark:text-slate-400">
            No mail yet. Submit a matter to generate approval notifications.
          </li>
        )}
      </ul>
    </div>
  );
}
